import { observer } from "mobx-react";
import { ReactNode } from "react";
import { Alert } from "@/components-v2/Alert";
import { Box, Card, Grid, VBox } from "@/components-v2/Box";
import { Button, IconButton, LinkButton } from "@/components-v2/Button";
import { MCheckbox } from "@/components-v2/Checkbox";
import { MDateInput } from "@/components-v2/DateInput";
import { Dialog } from "@/components-v2/Dialog";
import { DropdownMenu, DropdownMenuItems } from "@/components-v2/Dropdown";
import { MRadioButtonGroup } from "@/components-v2/RadioButton";
import { Select } from "@/components-v2/Select";
import { Tag } from "@/components-v2/Tag";
import { MTextArea } from "@/components-v2/TextArea";
import { MTextInput } from "@/components-v2/TextInput";
import { lookupShareTypeName, ShareType } from "@/common/share";
import { TMessage } from "@/i18n/tt";
import { useDispatch, useRootStore, useTranslation, useUserStore } from "@/stores/RootStoreContext";
import { formatUserName } from "@/types/UserInfo";
import { today, toLocalDateISOString } from "@/utils/Dates";
import { when } from "@/utils/functions";
import {
  closeDeleteExerciseRequest,
  closeExerciseRequestForm,
  closeExerciseRequestReview,
  closeSentNotification,
  DeleteExerciseRequestState,
  ExerciseActionScope,
  exerciseRequestCandidates,
  ExerciseRequestCandidate,
  exerciseRequestFormComplete,
  ExerciseRequestFormState,
  exerciseRequestIssues,
  exerciseRequestMenuItems,
  administratorName,
  exerciseRequestNoticePreview,
  requesterName,
  exerciseRequestStage,
  exerciseRequestReviewComplete,
  ExerciseRequestReviewState,
  ExerciseRequestView,
  ExerciseWindow,
  exerciseStageTag,
  HolderExerciseRequestScope,
  holderRequestMenuItems,
  holderRequestPage,
  HolderRequestPage,
  holderRequestsCollapsed,
  setHolderRequestsCollapsed,
  declinedRequestDaysLeft,
  requestDisplayStatus,
  submitHolderExerciseRequest,
  submitHolderWithdrawRequest,
  openSentNotification,
  OptionGrantDetails,
  OptionPlan,
  prepExerciseRequestSummary,
  reviewNotifiesSomeone,
  selectableRequestCandidates,
  SentNotificationState,
  setExerciseRequestTranche,
  startExerciseRequestReview,
  submitDeleteExerciseRequest,
  submitExerciseRequestForm,
  submitExerciseRequestReview,
} from "./optionsDomain";
import { InstrumentHolder } from "./instrumentHolders";

function SummaryRow({
  label,
  value,
  strong,
}: {
  label: TMessage;
  value?: ReactNode;
  strong?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Grid cols={2} gap={"lg"} fontWeight={strong ? "bold" : undefined}>
      <span>{t(label)}</span>
      <span>{value ?? "–"}</span>
    </Grid>
  );
}

const useWindowLabel = () => {
  const { f } = useTranslation();
  return (window?: ExerciseWindow): string | undefined =>
    window === undefined
      ? undefined
      : `${f.formatDate(window.fromDate)} – ${f.formatDate(window.toDate)}`;
};

export const ExerciseRequestDialogs = observer(function ExerciseRequestDialogs({
  grants,
  plans,
  holders,
  shareTypes,
  today,
}: {
  grants: OptionGrantDetails[];
  plans: OptionPlan[];
  holders: InstrumentHolder[];
  shareTypes: ShareType[];
  today: string;
}) {
  const { optionsState } = useRootStore();
  return (
    <>
      {optionsState?.exerciseRequestForm && (
        <ExerciseRequestFormDialog
          form={optionsState.exerciseRequestForm}
          candidates={exerciseRequestCandidates(grants, plans, holders)}
          shareTypes={shareTypes}
          today={today}
        />
      )}
      {optionsState?.exerciseRequestReview && (
        <ExerciseRequestReviewDialog
          review={optionsState.exerciseRequestReview}
          shareTypes={shareTypes}
        />
      )}
      {optionsState?.deleteExerciseRequest && (
        <DeleteExerciseRequestDialog state={optionsState.deleteExerciseRequest} />
      )}
      {optionsState?.sentNotification && (
        <SentNotificationDialog state={optionsState.sentNotification} />
      )}
    </>
  );
});

export const ExerciseRequestFormDialog = observer(function ExerciseRequestFormDialog({
  form,
  candidates,
  shareTypes,
  today,
  holderScope,
}: {
  form: ExerciseRequestFormState;
  candidates: ExerciseRequestCandidate[];
  shareTypes: ShareType[];
  today: string;
  holderScope?: HolderExerciseRequestScope;
}) {
  const userStore = useUserStore();
  const { t, tf, f, lang } = useTranslation();
  const dispatch = useDispatch();
  const windowLabel = useWindowLabel();
  const summary = prepExerciseRequestSummary(form, candidates);
  const issues = exerciseRequestIssues(summary, form.quantity.value, today);
  const selectable = selectableRequestCandidates(candidates, today);
  const candidate = summary?.candidate;
  const shareTypeId = candidate?.tranche.shareTypeId;
  const shareTypeName =
    shareTypeId === undefined ? undefined : lookupShareTypeName(shareTypeId, shareTypes, lang);

  return (
    <Dialog
      maxWidth={"sm"}
      title={{
        key:
          form.requestId !== undefined
            ? "options.edit-exercise-request-title"
            : holderScope !== undefined
              ? "options.request-exercise"
              : "options.register-exercise-request",
      }}
      onCancel={[closeExerciseRequestForm()]}
      onSubmit={() =>
        dispatch(
          holderScope === undefined
            ? submitExerciseRequestForm(userStore.orgNumber, form, candidates, today)
            : submitHolderExerciseRequest(holderScope, form, candidates, today),
        )
      }
      submitResult={form.submitResult}
      submitLabel={{
        key:
          holderScope === undefined || form.requestId !== undefined
            ? "save"
            : "options.send-exercise-request",
      }}
      isSubmitDisabled={!exerciseRequestFormComplete(form, summary, today)}
    >
      <VBox gap={"lg"} marginY={"sm"}>
        {!form.isPreselected && (
          <Select<ExerciseRequestCandidate>
            label={t("options.option-grant")}
            placeholder={t("choose")}
            value={candidate}
            items={selectable}
            fullWidth
            idFn={(item) => item.tranche.optionTrancheId}
            labelFn={(item) => ({ translated: `${item.holderName} – ${tf(item.program)}` })}
            onSelect={(item) => [setExerciseRequestTranche(item.tranche.optionTrancheId)]}
          />
        )}
        {candidate !== undefined && (
          <>
            <p>
              {holderScope === undefined
                ? t({
                    key: "options.register-exercise-request-for",
                    params: { name: candidate.holderName },
                  })
                : t({
                    key: "options.request-exercise-description",
                    params: { program: candidate.program },
                  })}
            </p>
            {holderScope === undefined && (
              <Grid cols={1} colsMd={2}>
                <MDateInput label={t("options.request-date")} model={form.requestDate} />
              </Grid>
            )}
            <VBox gap={"sm"}>
              <strong>{t("options.exercise-count")}</strong>
              <MRadioButtonGroup
                model={form.quantity}
                labelFn={(quantity) =>
                  quantity === "ALL"
                    ? {
                        key: "options.all-available-options",
                        params: { available: { type: "number", value: summary?.available ?? 0 } },
                      }
                    : { key: "options.specify-quantity" }
                }
              />
              {form.quantity.value === "SPECIFY" && (
                <Grid cols={1} colsMd={2}>
                  <MTextInput model={form.count} align={"right"} autofocus />
                </Grid>
              )}
            </VBox>
            {issues.map((issue, index) => (
              <Alert key={index} type={"error"} icon message={issue} />
            ))}
            <VBox gap={"sm"}>
              <strong>{t("options.request-summary")}</strong>
              {holderScope === undefined ? (
                <>
                  <SummaryRow
                    label={{ key: "options.exercise-status-requested" }}
                    value={f.formatNumber(summary?.count ?? 0)}
                  />
                  <SummaryRow label={{ key: "shareType" }} value={shareTypeName} />
                  <SummaryRow label={{ key: "options.program" }} value={tf(candidate.program)} />
                  <SummaryRow
                    label={{ key: "options.exercise-cost" }}
                    value={f.formatAmount("NOK", summary?.cost ?? 0)}
                  />
                  <SummaryRow
                    label={{ key: "options.registered-by" }}
                    value={t({
                      key: "options.name-is-administrator",
                      params: { name: formatUserName(userStore.user) },
                    })}
                  />
                  <SummaryRow
                    label={{ key: "options.request-date" }}
                    value={summary?.requestDate && f.formatDate(summary.requestDate)}
                  />
                  <SummaryRow
                    label={{ key: "options.exercise-window" }}
                    value={windowLabel(summary?.window)}
                  />
                </>
              ) : (
                <>
                  <SummaryRow label={{ key: "options.program" }} value={tf(candidate.program)} />
                  <SummaryRow
                    label={{ key: "options.options-requested" }}
                    value={f.formatNumber(summary?.count ?? 0)}
                  />
                  <SummaryRow label={{ key: "shareType" }} value={shareTypeName} />
                  <SummaryRow
                    label={{ key: "options.exercise-price-per-share" }}
                    value={f.formatPrice("NOK", candidate.tranche.strikePrice)}
                  />
                  <SummaryRow
                    label={{ key: "options.total-exercise-cost" }}
                    value={f.formatAmount("NOK", summary?.cost ?? 0)}
                    strong
                  />
                </>
              )}
            </VBox>
          </>
        )}
      </VBox>
    </Dialog>
  );
});

const RequestTermsRows = observer(function RequestTermsRows({
  view,
  shareTypes,
}: {
  view: ExerciseRequestView;
  shareTypes: ShareType[];
}) {
  const { tf, f, lang } = useTranslation();
  const { request } = view;
  return (
    <>
      <SummaryRow label={{ key: "options.program" }} value={tf(view.program)} />
      <SummaryRow
        label={{ key: "options.options-requested" }}
        value={f.formatNumber(request.count)}
      />
      <SummaryRow
        label={{ key: "shareType" }}
        value={
          view.shareTypeId === undefined
            ? undefined
            : lookupShareTypeName(view.shareTypeId, shareTypes, lang)
        }
      />
      <SummaryRow
        label={{ key: "options.exercise-cost" }}
        value={f.formatAmount("NOK", request.count * view.strikePrice)}
      />
    </>
  );
});

const RequestDetails = observer(function RequestDetails({
  view,
  shareTypes,
}: {
  view: ExerciseRequestView;
  shareTypes: ShareType[];
}) {
  const { f } = useTranslation();
  const windowLabel = useWindowLabel();
  return (
    <>
      <RequestTermsRows view={view} shareTypes={shareTypes} />
      <SummaryRow
        label={{ key: "options.request-date" }}
        value={f.formatDate(view.request.requestDate)}
      />
      <SummaryRow label={{ key: "options.exercise-window" }} value={windowLabel(view.window)} />
    </>
  );
});

const ExerciseRequestReviewDialog = observer(function ExerciseRequestReviewDialog({
  review,
  shareTypes,
}: {
  review: ExerciseRequestReviewState;
  shareTypes: ShareType[];
}) {
  const { orgNumber, companyName } = useUserStore();
  const { t, lang } = useTranslation();
  const dispatch = useDispatch();
  const { view } = review;
  const decision = review.decision.value ?? "ACKNOWLEDGED";
  const isDeclined = decision === "REJECTED";
  const preview = exerciseRequestNoticePreview(view, decision, companyName ?? "");
  const notifies = reviewNotifiesSomeone(review);

  return (
    <Dialog
      maxWidth={"sm"}
      title={{ key: "options.review-request" }}
      onCancel={[closeExerciseRequestReview()]}
      onSubmit={() => dispatch(submitExerciseRequestReview(orgNumber, review, lang))}
      submitResult={review.submitResult}
      submitLabel={{
        key: isDeclined
          ? notifies
            ? "options.decline-and-notify"
            : "options.decline-request"
          : notifies
            ? "options.acknowledge-and-notify"
            : "options.acknowledge-request",
      }}
      isSubmitDisabled={!exerciseRequestReviewComplete(review)}
    >
      <VBox gap={"lg"} marginY={"sm"}>
        <VBox gap={"sm"}>
          <p>
            {t({ key: "options.review-request-description", params: { name: view.holderName } })}
          </p>
          <RequestDetails view={view} shareTypes={shareTypes} />
        </VBox>
        <VBox gap={"sm"}>
          <strong>{t("options.decision")}</strong>
          <MRadioButtonGroup model={review.decision} />
        </VBox>
        <VBox gap={"sm"}>
          <strong>{t("options.message-to-holder")}</strong>
          {view.holderEmail === undefined && (
            <Alert
              type={"info"}
              icon
              message={{ key: "options.holder-without-email", params: { name: view.holderName } }}
            />
          )}
          <span>{t("options.email-notification-preview")}</span>
          <VBox gap={"md"} padding={"md"} rounded border bg={"base"}>
            <p>
              {t(preview.greeting)}
              <br />
              {t(preview.decision)}
            </p>
            <p>{t(preview.note)}</p>
          </VBox>
          <MTextArea
            model={review.message}
            rows={3}
            placeholder={t(
              isDeclined ? "options.reason-for-declining" : "options.additional-message",
            )}
          />
        </VBox>
        <VBox gap={"sm"}>
          <span>{t("options.on-copy")}</span>
          <MCheckbox model={review.sendCopy} label={{ key: "options.send-a-copy-to" }} />
          {review.sendCopy.value && <MTextInput model={review.copyTo} placeholder={t("email")} />}
          <MCheckbox
            model={review.copyToAdministrators}
            label={{ key: "options.all-company-administrators" }}
          />
        </VBox>
      </VBox>
    </Dialog>
  );
});

export const DeleteExerciseRequestDialog = observer(function DeleteExerciseRequestDialog({
  state,
  holderScope,
}: {
  state: DeleteExerciseRequestState;
  holderScope?: HolderExerciseRequestScope;
}) {
  const { orgNumber } = useUserStore();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  return (
    <Dialog
      maxWidth={"sm"}
      title={{ key: "confirm" }}
      onCancel={[closeDeleteExerciseRequest()]}
      onSubmit={() =>
        dispatch(
          holderScope === undefined
            ? submitDeleteExerciseRequest(orgNumber, state)
            : submitHolderWithdrawRequest(holderScope, state),
        )
      }
      submitResult={state.submitResult}
      submitLabel={{ key: "confirm" }}
    >
      <p>{t("options.confirm-delete-exercise-request")}</p>
    </Dialog>
  );
});

const SentNotificationDialog = observer(function SentNotificationDialog({
  state,
}: {
  state: SentNotificationState;
}) {
  const { f } = useTranslation();
  const { notification } = state;
  return (
    <Dialog
      maxWidth={"sm"}
      title={{ key: "options.sent-notification" }}
      onCancel={[closeSentNotification()]}
      cancelLabel={{ key: "close" }}
    >
      <VBox gap={"md"} marginY={"sm"}>
        <VBox gap={"sm"}>
          <SummaryRow label={{ key: "to" }} value={notification.recipients.join(", ")} />
          <SummaryRow label={{ key: "sent" }} value={f.formatDateTime(state.sentAt)} />
          <SummaryRow label={{ key: "messaging.subject" }} value={notification.subject} />
        </VBox>
        <Box padding={"md"} rounded border bg={"base"}>
          <span className={"whitespace-pre-wrap"}>{notification.body}</span>
        </Box>
      </VBox>
    </Dialog>
  );
});

function CollapseToggle({ collapsed }: { collapsed: boolean }) {
  return (
    <IconButton
      icon={collapsed ? "caret-right" : "caret-down"}
      tooltip={{ key: collapsed ? "show" : "hide" }}
      onClick={[setHolderRequestsCollapsed(!collapsed)]}
    />
  );
}

function RequestPagerControls({ page }: { page: HolderRequestPage }) {
  const { t } = useTranslation();
  if (page.total <= 1) {
    return null;
  }
  return (
    <>
      <span>
        {t({
          key: "options.exercise-request-x-of-y",
          params: { number: page.number, total: page.total },
        })}
      </span>
      <Button
        variant={"secondary"}
        size={"sm"}
        icon={"caret-left"}
        tooltip={{ key: "previous" }}
        disabled={page.previous === undefined}
        onClick={page.previous === undefined ? undefined : [page.previous]}
      />
      <Button
        variant={"secondary"}
        size={"sm"}
        icon={"caret-right"}
        tooltip={{ key: "next" }}
        disabled={page.next === undefined}
        onClick={page.next === undefined ? undefined : [page.next]}
      />
    </>
  );
}

const ExerciseRequestsCard = observer(function ExerciseRequestsCard({
  views,
  actions,
  children,
  highlightPending,
  isCollapsedByDefault = false,
}: {
  views: ExerciseRequestView[];
  actions: (view: ExerciseRequestView) => ReactNode;
  children: (view: ExerciseRequestView) => ReactNode;
  highlightPending?: boolean;
  isCollapsedByDefault?: boolean;
}) {
  const { t } = useTranslation();
  const { optionsState } = useRootStore();
  const page = holderRequestPage(views, optionsState?.holderRequestIndex);
  if (page === undefined) {
    return null;
  }
  const { view } = page;
  const collapsed = holderRequestsCollapsed(optionsState, isCollapsedByDefault);
  const stage = exerciseRequestStage(requestDisplayStatus(view.request.status));

  return (
    <Card gap={"md"} bg={highlightPending && view.request.status === "REQUESTED" ? "alt2" : true}>
      <Box justifyContent={"space-between"} alignItems={"center"} gap={"md"}>
        <Box alignItems={"center"} gap={"md"}>
          <CollapseToggle collapsed={collapsed} />
          <strong>{t("options.exercise-request")}</strong>
          {stage !== undefined && <Tag {...exerciseStageTag(stage)} />}
        </Box>
        <Box alignItems={"center"} gap={"sm"}>
          <RequestPagerControls page={page} />
          {actions(view)}
        </Box>
      </Box>
      {!collapsed && children(view)}
    </Card>
  );
});

function RequestMenu({ items }: { items: DropdownMenuItems }) {
  return items.length === 0 ? null : (
    <DropdownMenu button={{ type: "dotdotdot" }} alignment={"end"} items={items} />
  );
}

export const AdminExerciseRequestsCard = observer(function AdminExerciseRequestsCard({
  views,
  shareTypes,
  scope,
}: {
  views: ExerciseRequestView[];
  shareTypes: ShareType[];
  scope: ExerciseActionScope;
}) {
  return (
    <ExerciseRequestsCard
      views={views}
      highlightPending
      actions={(view) => (
        <>
          {view.request.status === "REQUESTED" && (
            <Button
              variant={"primary"}
              label={{ key: "options.review-request" }}
              onClick={[startExerciseRequestReview(view)]}
            />
          )}
          <RequestMenu items={exerciseRequestMenuItems(view, scope)} />
        </>
      )}
    >
      {(view) => <AdminRequestDetails view={view} shareTypes={shareTypes} />}
    </ExerciseRequestsCard>
  );
});

const AdminRequestDetails = observer(function AdminRequestDetails({
  view,
  shareTypes,
}: {
  view: ExerciseRequestView;
  shareTypes: ShareType[];
}) {
  const { t, f } = useTranslation();
  const windowLabel = useWindowLabel();
  const { request, decision } = view;
  const isDeclined = decision?.status === "REJECTED";
  const notification = decision?.notification;
  return (
    <VBox gap={"sm"}>
      <RequestTermsRows view={view} shareTypes={shareTypes} />
      <SummaryRow label={{ key: "options.exercise-window" }} value={windowLabel(view.window)} />
      <SummaryRow
        label={{ key: "options.requested-by" }}
        value={when(requesterName(view), (name) => t(name))}
      />
      <SummaryRow
        label={{ key: "options.request-date" }}
        value={f.formatDate(request.requestDate)}
      />
      {decision !== undefined && (
        <>
          <SummaryRow
            label={{ key: isDeclined ? "options.declined-by" : "options.acknowledged-by" }}
            value={when(administratorName(decision.changedByName), (name) => t(name))}
          />
          <SummaryRow
            label={{ key: isDeclined ? "options.declined-on" : "options.acknowledged-on" }}
            value={f.formatDate(toLocalDateISOString(new Date(decision.changedAt)))}
          />
        </>
      )}
      {decision !== undefined && notification !== undefined && (
        <SummaryRow
          label={{ key: "options.notification" }}
          value={
            <LinkButton
              label={{ key: "options.view-sent-notification" }}
              onClick={[openSentNotification(notification, decision.changedAt)]}
            />
          }
        />
      )}
    </VBox>
  );
});

export const HolderExerciseRequestsCard = observer(function HolderExerciseRequestsCard({
  views,
  shareTypes,
  companyName,
  canManage,
}: {
  views: ExerciseRequestView[];
  shareTypes: ShareType[];
  companyName: string;
  canManage: boolean;
}) {
  return (
    <ExerciseRequestsCard
      views={views}
      isCollapsedByDefault
      actions={(view) => <RequestMenu items={canManage ? holderRequestMenuItems(view) : []} />}
    >
      {(view) => (
        <>
          <HolderRequestMessage
            view={view}
            companyName={companyName}
            today={toLocalDateISOString(today())}
          />
          {view.request.status !== "REJECTED" && (
            <HolderRequestSummary view={view} shareTypes={shareTypes} />
          )}
        </>
      )}
    </ExerciseRequestsCard>
  );
});

const HolderRequestSummary = observer(function HolderRequestSummary({
  view,
  shareTypes,
}: {
  view: ExerciseRequestView;
  shareTypes: ShareType[];
}) {
  const { t, tf, f, lang } = useTranslation();
  const { user } = useUserStore();
  const { request } = view;
  const requester = request.history[0];
  const requesterName =
    requester?.changedByName !== undefined &&
    user !== null &&
    requester.changedBy === String(user.userId)
      ? t({ key: "options.name-is-you", params: { name: requester.changedByName } })
      : requester?.changedByName;
  return (
    <VBox gap={"sm"}>
      <strong>{t("options.request-summary")}</strong>
      <SummaryRow label={{ key: "options.program" }} value={tf(view.program)} />
      <SummaryRow
        label={{ key: "options.options-requested" }}
        value={f.formatNumber(request.count)}
      />
      <SummaryRow
        label={{ key: "shareType" }}
        value={
          view.shareTypeId === undefined
            ? undefined
            : lookupShareTypeName(view.shareTypeId, shareTypes, lang)
        }
      />
      <SummaryRow
        label={{ key: "options.exercise-price-per-share" }}
        value={f.formatPrice("NOK", view.strikePrice)}
      />
      <SummaryRow
        label={{ key: "options.total-exercise-cost" }}
        value={f.formatAmount("NOK", request.count * view.strikePrice)}
        strong
      />
      <SummaryRow label={{ key: "options.requested-by" }} value={requesterName} />
      <SummaryRow
        label={{ key: "options.request-date" }}
        value={f.formatDate(request.requestDate)}
      />
    </VBox>
  );
});

function HolderRequestMessage({
  view,
  companyName,
  today,
}: {
  view: ExerciseRequestView;
  companyName: string;
  today: string;
}) {
  const { t } = useTranslation();
  const company = { company: companyName };
  const { decision } = view;
  switch (requestDisplayStatus(view.request.status)) {
    case "REQUESTED":
      return <p>{t({ key: "options.exercise-request-submitted-info", params: company })}</p>;
    case "ACKNOWLEDGED":
      return (
        <VBox gap={"md"}>
          <p className={"whitespace-pre-line"}>
            {t({ key: "options.exercise-request-acknowledged-info", params: company })}
          </p>
          <p>{t("options.exercise-request-shown-until-completed")}</p>
        </VBox>
      );
    case "REJECTED": {
      const preview = exerciseRequestNoticePreview(view, "REJECTED", companyName);
      const commenter = administratorName(decision?.changedByName);
      return (
        <VBox gap={"md"}>
          <p>
            {t(preview.decision)} {t(preview.note)}
            {decision?.remarks !== undefined && commenter !== undefined && (
              <>
                <br />
                {t({
                  key: "options.exercise-request-comment",
                  params: { name: commenter, message: decision.remarks },
                })}
              </>
            )}
          </p>
          <p>
            {t({
              key: "options.exercise-request-hidden-in",
              params: { count: declinedRequestDaysLeft(view, today) ?? 0 },
            })}
          </p>
        </VBox>
      );
    }
    default:
      return null;
  }
}
